import React from "react";
import { Outlet } from "react-router-dom";
import { useLoginSession } from "../LoginSession";

/**
 * Logs out the user if a non-superuser tries to access a superuser page.
 *
 * @note The value that indicates whether a user is a superuser is managed by {@link useLoginSession}.
 *
 * @example
 * ```
 * // Wrapping a component you want to protect
 * <SuperuserComponent>
 *  <ProtectedComponent />
 * </SuperuserComponent>
 * ```
 *
 * @param {object} props
 * @param {React.ReactNode} [props.children] The content to render.
 */
export function SuperuserComponent({ children }) {
    const { userInfo, isStale, logout } = useLoginSession(true);

    if (userInfo && !isStale && !userInfo.superuser) {
        logout();
    }

    return children;
}

/**
 * Wraps {@link SuperuserComponent} around a child route.
 *
 * @example
 * ```
 * // Protecting child routes
 * <Route path="/protected-parent-route" element={<SuperuserComponent />}>
 *  <Route path="protected-child-a" element={<ProtectedChildComponentA />} />
 *  <Route path="protected-child-b" element={<ProtectedChildComponentB />} />
 * </Route>
 * ```
 */
export function SuperuserRouteWrapper() {
    return (
        <SuperuserComponent>
            <Outlet />
        </SuperuserComponent>
    );
}
